import * as i0 from '@angular/core';
import { InjectionToken, Injectable, SkipSelf, Optional, Directive, Input, Inject, inject, HostListener, NgModule, Self } from '@angular/core';
import { Logger, DevTrackingClient, TrackingUtil, _Assert, ApplicationTracker } from '@yggdrasil/core';
import { BehaviorSubject, firstValueFrom, Observable, isObservable } from 'rxjs';

/**
 * Provide this token to configure the core tracking client
 * used by the services & directives.
 */
const TRACKING_CLIENT = new InjectionToken("TRACKING_CLIENT", {
    providedIn: "root",
    factory() {
        const log = Logger.create("Angular");
        log.warn(`@yggdrasil/angular has not been provided with a TRACKING_CLIENT. Falling back to using a DevTrackingClient`);
        return new DevTrackingClient(undefined, { enableOnWebdriver: true });
    },
});
/**
 * This can be used to set/get the ApplicationTracker for the current DOM tree.
 * The context is usually provided by the ygTrackApp directive, although a fallback
 * context is also provided at the "root". Note that the fallback context is only
 * a guess based on previous events, so it may not be accurate.
 */
class YgApplicationTrackerContext {
    constructor(parentContext) {
        this.parentContext = parentContext;
        this.subject = new BehaviorSubject(null);
    }
    getTracker() {
        return firstValueFrom(this.subject.pipe(filterNullable()));
    }
    async getParentTracker() {
        if (this.parentContext) {
            return this.parentContext.getTracker();
        }
        else {
            return null;
        }
    }
    setTracker(tracker) {
        this.subject.next(tracker);
        if (this.parentContext) {
            this.setRootTracker(tracker);
        }
    }
    setRootTracker(tracker) {
        if (this.parentContext) {
            this.parentContext.setRootTracker(tracker);
        }
        else {
            this.subject.next(tracker);
        }
    }
}
YgApplicationTrackerContext.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgApplicationTrackerContext, deps: [{ token: YgApplicationTrackerContext, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.Injectable });
YgApplicationTrackerContext.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgApplicationTrackerContext, providedIn: "root" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgApplicationTrackerContext, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root",
                }]
        }], ctorParameters: function () { return [{ type: YgApplicationTrackerContext, decorators: [{
                    type: SkipSelf
                }, {
                    type: Optional
                }] }]; } });
/**
 * This can be used to set/get the ViewTracker for the current DOM tree.
 * The context is usually provided by the ygTrackView directive, although a fallback
 * context is also provided at the "root". Note that the fallback context is only
 * a guess based on previous events, so it may not be accurate.
 */
class YgViewTrackerContext {
    constructor(parentContext) {
        this.parentContext = parentContext;
        this.subject = new BehaviorSubject(null);
    }
    getTracker() {
        return firstValueFrom(this.subject.pipe(filterNullable()));
    }
    async getParentTracker() {
        if (this.parentContext) {
            return this.parentContext.getTracker();
        }
        else {
            return null;
        }
    }
    setTracker(tracker) {
        this.subject.next(tracker);
        if (this.parentContext) {
            this.setRootTracker(tracker);
        }
    }
    setRootTracker(tracker) {
        if (this.parentContext) {
            this.parentContext.setRootTracker(tracker);
        }
        else {
            this.subject.next(tracker);
        }
    }
}
YgViewTrackerContext.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgViewTrackerContext, deps: [{ token: YgViewTrackerContext, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.Injectable });
YgViewTrackerContext.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgViewTrackerContext, providedIn: "root" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgViewTrackerContext, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root",
                }]
        }], ctorParameters: function () { return [{ type: YgViewTrackerContext, decorators: [{
                    type: SkipSelf
                }, {
                    type: Optional
                }] }]; } });
/**
 * An RXJS operator that filters null & undefined values.
 */
function filterNullable() {
    return function nonNullable(source) {
        return new Observable((subscriber) => {
            return source.subscribe({
                next(value) {
                    if (value !== null && value !== undefined) {
                        subscriber.next(value);
                    }
                },
                complete() {
                    subscriber.complete();
                },
                error(err) {
                    subscriber.error(err);
                },
            });
        });
    };
}
// eslint-disable-next-line @angular-eslint/directive-class-suffix
class TrackDirectiveBase {
    constructor() {
        this._attributes = {};
        this._tags = [];
        this._features = [];
        this._siteWorkflows = [];
        this._globalWorkflows = [];
    }
    set ygAttributes(attributes) {
        this._attributes = attributes;
    }
    set ygTags(tags) {
        this._tags = this.processTags(tags);
    }
    set ygType(type) {
        if (type) {
            this._type = type;
        }
    }
    set ygFeatures(tags) {
        this._features = this.processTags(tags);
    }
    set ygSiteWorkflows(tags) {
        this._siteWorkflows = this.processTags(tags);
    }
    set ygGlobalWorkflows(tags) {
        this._globalWorkflows = this.processTags(tags);
    }
    getFeatures() {
        return this._features;
    }
    getSiteWorkflows() {
        return this._siteWorkflows;
    }
    getGlobalWorkflows() {
        return this._globalWorkflows;
    }
    getAttributes() {
        return this._attributes;
    }
    getTags() {
        return this._tags;
    }
    getType() {
        return this._type;
    }
    timestamp() {
        return TrackingUtil.timestamp();
    }
    processTags(tags) {
        let tagList = [];
        if (typeof tags === "string") {
            tagList = tags.trim().split(/\s+/);
        }
        else if (Array.isArray(tags)) {
            tagList = tags;
        }
        else {
            throw new TypeError(`tags must be a string or an array of strings, got ${typeof tags}`);
        }
        return [...new Set(tagList)];
    }
}
TrackDirectiveBase.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: TrackDirectiveBase, deps: [], target: i0.ɵɵFactoryTarget.Directive });
TrackDirectiveBase.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.5", type: TrackDirectiveBase, inputs: { ygAttributes: "ygAttributes", ygTags: "ygTags", ygType: "ygType", ygFeatures: "ygFeatures", ygSiteWorkflows: "ygSiteWorkflows", ygGlobalWorkflows: "ygGlobalWorkflows" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: TrackDirectiveBase, decorators: [{
            type: Directive
        }], propDecorators: { ygAttributes: [{
                type: Input
            }], ygTags: [{
                type: Input
            }], ygType: [{
                type: Input
            }], ygFeatures: [{
                type: Input
            }], ygSiteWorkflows: [{
                type: Input
            }], ygGlobalWorkflows: [{
                type: Input
            }] } });
class Assert extends _Assert {
    static requiredInput(inputName, value) {
        Assert.nonEmptyString(value, `Input [${inputName}] must be a non-empty string`);
    }
}

var name = "@yggdrasil/angular";
var version = "0.5.6";
var packageInfo = {
	name: name,
	version: version
};

/**
 * Low-level service for interacting directly with the TrackingClient.
 *
 * In general, it is better to stick with the directives or the ApplicationTracker/ViewTracker.
 * However this service can be useful if you for instance *only* wish to track application
 * access and don't care about views, events etc.
 */
class YgClientService {
    constructor(client) {
        this.clientPromise = this.toPromise(client);
    }
    trackApplicationStart(app) {
        this.clientPromise.then((client) => client.trackApplicationStart(app));
    }
    trackApplicationEnd(app) {
        this.clientPromise.then((client) => client.trackApplicationEnd(app));
    }
    trackApplicationEvent(event) {
        this.clientPromise.then((client) => client.trackApplicationEvent(event));
    }
    trackViewStart(view) {
        this.clientPromise.then((client) => client.trackViewStart(view));
    }
    trackViewEnd(view) {
        this.clientPromise.then((client) => client.trackViewEnd(view));
    }
    trackEvent(event) {
        this.clientPromise.then((client) => client.trackEvent(event));
    }
    trackContent(content) {
        this.clientPromise.then((client) => client.trackContent(content));
    }
    // empty function added to avoid compile error there is no load template need in ng module
    loadTemplate(request) {
        return this.clientPromise.then((client) => client.loadTemplate(request));
    }
    setSessionStore(sessionStorage) {
        return;
    }
    getConfig() {
        return {};
    }
    toPromise(obj) {
        if (isObservable(obj)) {
            return firstValueFrom(obj);
        }
        else {
            return Promise.resolve(obj);
        }
    }
}
YgClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgClientService, deps: [{ token: TRACKING_CLIENT }], target: i0.ɵɵFactoryTarget.Injectable });
YgClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgClientService, providedIn: "root" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgClientService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root",
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [TRACKING_CLIENT]
                }] }]; } });

/**
 * Tracks when the application is viewed. This directive is required to
 * provide the application context for all views and events, and should be placed
 * in the application root component.
 *
 * Required inputs:
 * - **ygTrackApp (name):** The human-friendly application name.
 * - **ygAppId:** A unique identifier for the application.
 *
 * Optional inputs:
 * - **ygTags:** mark it with a tag.
 * - **ygFeatures:** mark it with a feature tag, similar to tags.
 * - **ygSiteWorkflows:** mark it with a workflow tag of a site, similar to tags.
 * - **ygGlobalWorkflows:** mark it with a workflow tag of a global workflow, similar to tags.
 *
 * @example
 * ```html
 * 	<app-container ygTrackApp="Grocery Store" ygAppId="com.store.grocery">
 * 		<!-- The "Checkout" view will be associated with the "Grocery Store" application -->
 * 		<checkout-page ygTrackView="Checkout"></checkout-page>
 * 	</app-container>
 * ```
 */
class YgTrackAppDirective extends TrackDirectiveBase {
    constructor(element, client, context) {
        super();
        this.element = element;
        this.client = client;
        this.context = context;
        this.parentTrackAppDirective = inject(YgTrackAppDirective, {
            skipSelf: true,
            optional: true,
        });
        this.observer = new IntersectionObserver(([record]) => {
            if (record.isIntersecting) {
                this.onAppStart();
            }
            else {
                this.onAppEnd();
            }
        });
    }
    ngAfterViewInit() {
        this.observer.observe(this.element.nativeElement);
    }
    ngOnDestroy() {
        this.observer.disconnect();
        this.onAppEnd(); // The observer will not run after the component is destroyed, so this is needed
    }
    async onAppStart() {
        if (!this.appTracker) {
            Assert.requiredInput("ygAppId", this.ygAppId);
            Assert.requiredInput("ygTrackApp", this.name);
            const parentTracker = await this.getParentTracker();
            if (parentTracker) {
                this.appTracker = parentTracker.createChild({
                    hostname: location.hostname,
                    identifier: this.ygAppId,
                    name: this.name,
                    attributes: this.getAttributes(),
                    tags: this.getTags(),
                    type: this.getType(),
                    version: this.ygAppVersion,
                    features: this.getFeatures(),
                    siteWorkflows: this.getSiteWorkflows(),
                    globalWorkflows: this.getGlobalWorkflows(),
                });
            }
            else {
                this.appTracker = new ApplicationTracker({
                    hostname: location.hostname,
                    identifier: this.ygAppId,
                    name: this.name,
                    trackingClient: this.client,
                    attributes: this.getAttributes(),
                    tags: this.getTags(),
                    type: this.getType(),
                    version: this.ygAppVersion,
                    features: this.getFeatures(),
                    siteWorkflows: this.getSiteWorkflows(),
                    globalWorkflows: this.getGlobalWorkflows(),
                });
            }
            this.appTracker.trackStart();
            this.context.setTracker(this.appTracker);
        }
    }
    onAppEnd() {
        if (this.appTracker) {
            this.appTracker.trackEnd();
            this.appTracker = undefined;
        }
    }
    async getParentTracker() {
        if (this.parentTrackAppDirective) {
            // There is a parent application, so we wait for it to start and get the tracker.
            return this.context.getParentTracker();
        }
        else {
            // There is no parent application (in the injection tree), so we assume this is meant to be the root app with no parent.
            return null;
        }
    }
}
YgTrackAppDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgTrackAppDirective, deps: [{ token: i0.ElementRef }, { token: YgClientService }, { token: YgApplicationTrackerContext }], target: i0.ɵɵFactoryTarget.Directive });
YgTrackAppDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.5", type: YgTrackAppDirective, selector: "[ygTrackApp]", inputs: { name: ["ygTrackApp", "name"], ygAppId: "ygAppId", ygAppVersion: "ygAppVersion" }, providers: [YgApplicationTrackerContext], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgTrackAppDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[ygTrackApp]",
                    providers: [YgApplicationTrackerContext],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: YgClientService }, { type: YgApplicationTrackerContext }]; }, propDecorators: { name: [{
                type: Input,
                args: ["ygTrackApp"]
            }], ygAppId: [{
                type: Input
            }], ygAppVersion: [{
                type: Input
            }] } });

/**
 * Tracks when the element is clicked.
 *
 * Optional inputs:
 * - **ygBounceEvent:** boolean which marks the event is a bouncing event.
 * - **ygTags:** mark it with a tag.
 * - **ygFeatures:** mark it with a feature tag, similar to tags.
 * - **ygSiteWorkflows:** mark it with a workflow tag of a site, similar to tags.
 * - **ygGlobalWorkflows:** mark it with a workflow tag of a global workflow, similar to tags.
 *
 * @example
 * ```html
 * 	<cart-page ygTrackView="Cart">
 * 		<button ygTrackClick="Delete">
 * 			<span>Delete item from cart</span>
 * 		</button>
 *    <button ygTrackClick="Close" ygBounceEvent>
 * 			<span>Close View</span>
 * 		</button>
 * 	</cart-page>
 * ```
 */
class YgTrackClickDirective extends TrackDirectiveBase {
    set ygBounceEvent(value) {
        if (typeof value === "string") {
            this._bounce = true;
        }
        else {
            this._bounce = value || true;
        }
    }
    getBounce() {
        return this._bounce || false;
    }
    async click() {
        Assert.requiredInput("ygTrackClick", this.name);
        const view = await this.context.getTracker();
        const type = this.getType() || "click";
        view.trackEvent(this.name, type, this.getAttributes(), this.getTags(), this.getFeatures(), this.getSiteWorkflows(), this.getGlobalWorkflows(), this.getBounce());
    }
    constructor(context) {
        super();
        this.context = context;
    }
}
YgTrackClickDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgTrackClickDirective, deps: [{ token: YgViewTrackerContext }], target: i0.ɵɵFactoryTarget.Directive });
YgTrackClickDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.5", type: YgTrackClickDirective, selector: "[ygTrackClick]", inputs: { name: ["ygTrackClick", "name"], ygBounceEvent: "ygBounceEvent" }, host: { listeners: { "click": "click()" } }, usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgTrackClickDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[ygTrackClick]",
                }]
        }], ctorParameters: function () { return [{ type: YgViewTrackerContext }]; }, propDecorators: { name: [{
                type: Input,
                args: ["ygTrackClick"]
            }], ygBounceEvent: [{
                type: Input
            }], click: [{
                type: HostListener,
                args: ["click"]
            }] } });

/**
 * Track when content is loaded.
 *
 * This is similar to the TrackViewDirective, but it will only track once when the element is added to the page,
 * regardless of whether it is visible or not.
 *
 * Optional inputs:
 * - **ygTags:** mark it with a tag.
 * - **ygFeatures:** mark it with a feature tag, similar to tags.
 * - **ygSiteWorkflows:** mark it with a workflow tag of a site, similar to tags.
 * - **ygGlobalWorkflows:** mark it with a workflow tag of a global workflow, similar to tags.
 *
 * @example
 * ```html
 * 	<ul class="search-results">
 * 		<li *ngFor="let item of searchResults" [ygTrackContent]="item" ygType="search_result">{{item}}</li>
 * 	</ul>
 * ```
 */
class YgTrackContentdDirective extends TrackDirectiveBase {
    constructor(context) {
        super();
        this.context = context;
        this.tracked = false;
    }
    ngAfterViewInit() {
        if (this.name && !this.tracked) {
            this.trackContentLoaded();
            this.tracked = true;
        }
    }
    ngOnChanges(changes) {
        if (changes.name?.currentValue && !this.tracked) {
            this.trackContentLoaded();
            this.tracked = true;
        }
    }
    async trackContentLoaded() {
        const view = await this.context.getTracker();
        const type = this.getType();
        Assert.requiredInput("ygTrackContent", this.name);
        Assert.requiredInput("ygType", type);
        view.trackContent(this.name, type, this.getAttributes(), this.getTags(), this.getFeatures(), this.getSiteWorkflows(), this.getGlobalWorkflows());
    }
}
YgTrackContentdDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgTrackContentdDirective, deps: [{ token: YgViewTrackerContext }], target: i0.ɵɵFactoryTarget.Directive });
YgTrackContentdDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.5", type: YgTrackContentdDirective, selector: "[ygTrackContent]", inputs: { name: ["ygTrackContent", "name"] }, usesInheritance: true, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgTrackContentdDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[ygTrackContent]",
                }]
        }], ctorParameters: function () { return [{ type: YgViewTrackerContext }]; }, propDecorators: { name: [{
                type: Input,
                args: ["ygTrackContent"]
            }] } });

/**
 * Track when an input field changes value.
 *
 * @example
 * ```html
 * 	<store-page ygTrackView="Store">
 * 		<label>Search</label>
 * 		<input type="search" ygTrackField="Items">
 *
 * 		<input type="checkbox" ygTrackField="Expired">
 * 		<label>Include expired items in search results</label>
 * 	</store-page>
 * ```
 */
class YgTrackFieldDirective extends TrackDirectiveBase {
    change() {
        if (this.element.nativeElement.value) {
            this.trackFieldValue(this.element.nativeElement.value);
        }
    }
    async trackFieldValue(value) {
        const view = await this.viewContext.getTracker();
        const type = this.getType() || this.element.nativeElement.type || "change";
        Assert.requiredInput("ygTrackField", this.name);
        view.trackEvent(this.name, `field.${type}`, {
            value,
            ...this.getAttributes(),
        }, this.getTags());
    }
    constructor(element, viewContext) {
        super();
        this.element = element;
        this.viewContext = viewContext;
    }
}
YgTrackFieldDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgTrackFieldDirective, deps: [{ token: i0.ElementRef }, { token: YgViewTrackerContext }], target: i0.ɵɵFactoryTarget.Directive });
YgTrackFieldDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.5", type: YgTrackFieldDirective, selector: "input[ygTrackField]", inputs: { name: ["ygTrackField", "name"] }, host: { listeners: { "change": "change()" } }, usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgTrackFieldDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "input[ygTrackField]",
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: YgViewTrackerContext }]; }, propDecorators: { name: [{
                type: Input,
                args: ["ygTrackField"]
            }], change: [{
                type: HostListener,
                args: ["change"]
            }] } });

/**
 * Tracks when the element is viewed. A View can be a full page, a dialog,
 * or any section of the application. This directive is required to provide
 * the view context for events and content, and should be placed on elements
 * that are either interesting to track, or needed for the context.
 *
 * Optional inputs:
 * - **ygTags:** mark it with a tag.
 * - **ygFeatures:** mark it with a feature tag, similar to tags.
 * - **ygSiteWorkflows:** mark it with a workflow tag of a site, similar to tags.
 * - **ygGlobalWorkflows:** mark it with a workflow tag of a global workflow, similar to tags.
 *
 * @example
 * ```html
 * 	<store-page ygTrackView="Store">
 * 		<!-- The "Checkout" event below will be associated with the "Store" view -->
 * 		<button ygTrackClick="Checkout">Go to checkout</button>
 * 	</store-page>
 * 	<checkout-page ygTrackView="Checkout"></checkout-page>
 * ```
 */
class YgTrackViewDirective extends TrackDirectiveBase {
    constructor(element, appContext, viewContext) {
        super();
        this.element = element;
        this.appContext = appContext;
        this.viewContext = viewContext;
        this.initiated = false;
        this.parentTrackViewDirective = inject(YgTrackViewDirective, {
            skipSelf: true,
            optional: true,
        });
        this.observer = new IntersectionObserver(([record]) => {
            if (record.isIntersecting) {
                this.onViewStart();
            }
            else {
                this.onViewEnd();
            }
        });
    }
    ngOnChanges(changes) {
        if (!this.initiated &&
            changes.name?.currentValue &&
            this.element.nativeElement) {
            // This covers the case where name is set after the directive has been initialized,
            // or when the name is initially null/undefined set by the async pipe.
            this.observer.observe(this.element.nativeElement);
            this.initiated = true;
        }
    }
    ngAfterViewInit() {
        if (this.name && !this.initiated) {
            this.observer.observe(this.element.nativeElement);
            this.initiated = true;
        }
    }
    ngOnDestroy() {
        this.observer.disconnect();
        this.onViewEnd(); // The observer will not run after the component is destroyed, so this is needed
    }
    async onViewStart() {
        if (!this.viewTracker) {
            const application = await this.appContext.getTracker();
            Assert.nonEmptyString(this.name, "name is not set");
            const parentTracker = await this.getParentTracker();
            const isParentInSameApp = application.getAppGuid() ===
                parentTracker?.getApplication().getAppGuid();
            if (parentTracker && isParentInSameApp) {
                this.viewTracker = parentTracker.createChild({
                    name: this.name,
                    attributes: this.getAttributes(),
                    tags: this.getTags(),
                    type: this.getType(),
                    features: this.getFeatures(),
                    siteWorkflows: this.getSiteWorkflows(),
                    globalWorkflows: this.getGlobalWorkflows(),
                    proxyClientRef: this.ygProxyClientRef,
                });
            }
            else {
                this.viewTracker = application.createView({
                    name: this.name,
                    attributes: this.getAttributes(),
                    tags: this.getTags(),
                    type: this.getType(),
                    features: this.getFeatures(),
                    siteWorkflows: this.getSiteWorkflows(),
                    globalWorkflows: this.getGlobalWorkflows(),
                    proxyClientRef: this.ygProxyClientRef,
                });
            }
            this.viewTracker.trackStart();
            this.viewContext.setTracker(this.viewTracker);
        }
    }
    async onViewEnd() {
        if (this.viewTracker) {
            this.viewTracker.trackEnd();
            this.viewTracker = undefined;
        }
    }
    async getParentTracker() {
        if (this.parentTrackViewDirective) {
            // There is a parent view, so we wait for it to start and get the tracker.
            return this.viewContext.getParentTracker();
        }
        else {
            // There is no parent view (in the injection tree), so we assume this is meant to be the root app with no parent.
            return null;
        }
    }
}
YgTrackViewDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgTrackViewDirective, deps: [{ token: i0.ElementRef }, { token: YgApplicationTrackerContext }, { token: YgViewTrackerContext }], target: i0.ɵɵFactoryTarget.Directive });
YgTrackViewDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.5", type: YgTrackViewDirective, selector: "[ygTrackView]", inputs: { name: ["ygTrackView", "name"], ygProxyClientRef: "ygProxyClientRef" }, providers: [YgViewTrackerContext], usesInheritance: true, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: YgTrackViewDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[ygTrackView]",
                    providers: [YgViewTrackerContext],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: YgApplicationTrackerContext }, { type: YgViewTrackerContext }]; }, propDecorators: { name: [{
                type: Input,
                args: ["ygTrackView"]
            }], ygProxyClientRef: [{
                type: Input
            }] } });

class NgYggdrasilModule {
    constructor(parentProvidedClient, selfProvidedClient) {
        if (parentProvidedClient && selfProvidedClient) {
            throw new Error("TRACKING_CLIENT should only be provided once");
        }
    }
}
NgYggdrasilModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: NgYggdrasilModule, deps: [{ token: TRACKING_CLIENT, optional: true, skipSelf: true }, { token: TRACKING_CLIENT, optional: true, self: true }], target: i0.ɵɵFactoryTarget.NgModule });
NgYggdrasilModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.5", ngImport: i0, type: NgYggdrasilModule, declarations: [YgTrackClickDirective,
        YgTrackViewDirective,
        YgTrackFieldDirective,
        YgTrackAppDirective,
        YgTrackContentdDirective], exports: [YgTrackClickDirective,
        YgTrackViewDirective,
        YgTrackFieldDirective,
        YgTrackAppDirective,
        YgTrackContentdDirective] });
NgYggdrasilModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: NgYggdrasilModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: NgYggdrasilModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        YgTrackClickDirective,
                        YgTrackViewDirective,
                        YgTrackFieldDirective,
                        YgTrackAppDirective,
                        YgTrackContentdDirective,
                    ],
                    imports: [],
                    exports: [
                        YgTrackClickDirective,
                        YgTrackViewDirective,
                        YgTrackFieldDirective,
                        YgTrackAppDirective,
                        YgTrackContentdDirective,
                    ],
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: SkipSelf
                }, {
                    type: Optional
                }, {
                    type: Inject,
                    args: [TRACKING_CLIENT]
                }] }, { type: undefined, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }, {
                    type: Inject,
                    args: [TRACKING_CLIENT]
                }] }]; } });
window.ygLibraryWrapper = "ANGULAR";
window.ygLibraryWrapperVersion = packageInfo.version;

/*
 * Public API Surface of ng-yggdrasil
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NgYggdrasilModule, TRACKING_CLIENT, YgApplicationTrackerContext, YgClientService, YgTrackAppDirective, YgTrackClickDirective, YgTrackContentdDirective, YgTrackFieldDirective, YgTrackViewDirective, YgViewTrackerContext };
